import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOnboardingBannerState, OnboardingStep } from '@/state/use-onboarding-banner-state'
import PwaInstallModal from './pwa-install-modal'
import NotificationsDeniedModal from './notifications-denied-modal'

interface OnboardingBannerProps {
	notificationChangeCounter: number
}

// OnboardingBanner shows one recommended action at a time: reloading for a newer deployed version,
// or a setup step (eg. PWA installation, notification enablement).
export default function OnboardingBanner({ notificationChangeCounter }: OnboardingBannerProps) {
	const [showPwaModal, setShowPwaModal] = useState(false)
	const [showDeniedModal, setShowDeniedModal] = useState(false)
	const navigate = useNavigate()
	const { isVisible, step, syncWithBackend } = useOnboardingBannerState()

	// Re-sync when Settings notifies us that notifications were enabled/disabled
	useEffect(() => {
		syncWithBackend()
	}, [notificationChangeCounter, syncWithBackend])

	if (!isVisible)
		return null

	switch (step) {
		case OnboardingStep.ReloadForUpdate:
			return (
				<div className="bg-yellow-500 text-white p-2 text-center text-sm z-40 flex items-center justify-center gap-2">
					<span>A new version is available.</span>
					<button
						onClick={() => window.location.reload()}
						className="underline font-semibold hover:text-blue-100"
					>
						Reload
					</button>
				</div>
			)

		case OnboardingStep.InstallPwa:
			return (
				<>
					<div className="bg-yellow-500 text-white p-2 text-center text-sm z-40 flex items-center justify-center gap-2">
						<span>Please add to home screen.</span>
						<button
							onClick={() => setShowPwaModal(true)}
							className="underline font-semibold hover:text-blue-100"
						>
							Why & how?
						</button>
					</div>
					<PwaInstallModal
						open={showPwaModal}
						onOpenChange={setShowPwaModal}
					/>
				</>
			)

		case OnboardingStep.NotificationsDenied:
			return (
				<>
					<div className="bg-yellow-500 text-white p-2 text-center text-sm z-40 flex items-center justify-center gap-2">
						<span>Notifications are blocked.</span>
						<button
							onClick={() => setShowDeniedModal(true)}
							className="underline font-semibold hover:text-blue-100"
						>
							How to fix?
						</button>
					</div>
					<NotificationsDeniedModal
						open={showDeniedModal}
						onOpenChange={setShowDeniedModal}
					/>
				</>
			)

		case OnboardingStep.EnableNotifications:
			return (
				<div className="bg-yellow-500 text-white p-2 text-center text-sm z-40 flex items-center justify-center gap-2">
					{/* <span>To get notified about new groups and messages, you need to</span> */}
					<span>Please enable notifications.</span>
					<button
						onClick={() => navigate('/settings?section=notifications')}
						className="underline font-semibold hover:text-blue-100"
					>
						Why & how?
					</button>
				</div>
			)

		default:
			return null
	}
}
